import { OutOfSyncBadge } from "@components/campaigns/OutOfSyncBadge";
import { formatRelative } from "@lib/format/relativeTime";
import {
  AlertCircle,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  FileBarChart,
  FileText,
  Filter,
  Grid3x3,
  Linkedin,
  Loader2,
  type LucideIcon,
  Map as MapIcon,
  ScrollText,
  ShieldCheck,
  Table2,
  Target,
  Users,
} from "lucide-react";
import { Link } from "react-router";

export type ArtefactKind =
  | "BRIEF"
  | "ROLE_BLUEPRINT"
  | "PERSONAS"
  | "MARKET_MAP"
  | "SCREENING_CRITERIA"
  | "LINKEDIN_JOB_POSTING"
  | "BRANDED_JOB_POSTING"
  | "SCREENING_INTERVIEW_GUIDE"
  | "ASSESSMENT_MATRIX"
  | "CANDIDATE_SCORING_MATRIX"
  | "ASSESSMENT"
  | "CLIENT_INTERVIEW_GUIDE"
  | "CLIENT_INTERVIEW"
  | "OFFER"
  | "LONGLIST_EXECUTIVE_SUMMARY"
  | "SHORTLIST_REPORT"
  | "CANDIDATE_REPORT";

export type ArtefactGroup = "DEFINE" | "SOURCE" | "ENGAGE" | "ASSESS";

export interface CampaignArtefact {
  id: string;
  kind: ArtefactKind;
  group: ArtefactGroup;
  title: string;
  status?: string | null;
  versionCount?: number | null;
  updatedAt?: string | null;
  url?: string | null;
  outOfSync?: boolean;
}

interface Props {
  artefacts: CampaignArtefact[];
}

const ICON_FOR_KIND: Record<ArtefactKind, LucideIcon> = {
  BRIEF: FileText,
  ROLE_BLUEPRINT: Target,
  PERSONAS: Users,
  MARKET_MAP: MapIcon,
  SCREENING_CRITERIA: Filter,
  LINKEDIN_JOB_POSTING: Linkedin,
  BRANDED_JOB_POSTING: FileText,
  SCREENING_INTERVIEW_GUIDE: ClipboardList,
  ASSESSMENT_MATRIX: Table2,
  CANDIDATE_SCORING_MATRIX: Grid3x3,
  ASSESSMENT: ShieldCheck,
  CLIENT_INTERVIEW_GUIDE: ClipboardCheck,
  CLIENT_INTERVIEW: Users,
  OFFER: ScrollText,
  LONGLIST_EXECUTIVE_SUMMARY: FileBarChart,
  SHORTLIST_REPORT: FileBarChart,
  CANDIDATE_REPORT: ScrollText,
};

// Per-candidate artefacts collapse into a single tile each so the page doesn't
// explode with one entry per candidate.
const AGGREGATE_KINDS: ReadonlySet<ArtefactKind> = new Set([
  "ASSESSMENT",
  "CLIENT_INTERVIEW",
  "OFFER",
  "CANDIDATE_REPORT",
]);

const GROUP_ORDER: ArtefactGroup[] = ["DEFINE", "SOURCE", "ENGAGE", "ASSESS"];

const GROUP_LABELS: Record<ArtefactGroup, string> = {
  DEFINE: "Define",
  SOURCE: "Source",
  ENGAGE: "Engage",
  ASSESS: "Assess",
};

const GROUP_BLURBS: Record<ArtefactGroup, string> = {
  DEFINE: "Brief the role and shape the search.",
  SOURCE: "Map the market and build the long list.",
  ENGAGE: "Reach out to candidates and the market.",
  ASSESS: "Score, interview, and present a shortlist.",
};

const AGGREGATE_TITLE: Partial<Record<ArtefactKind, string>> = {
  ASSESSMENT: "Candidate Assessments",
  CLIENT_INTERVIEW: "Client Interviews",
  OFFER: "Offers",
  CANDIDATE_REPORT: "Candidate Reports",
};

interface Tile {
  key: string;
  kind: ArtefactKind;
  title: string;
  url: string | null;
  status: string | null;
  versionCount: number | null;
  updatedAt: string | null;
  outOfSync: boolean;
  itemCount?: number;
  staleCount?: number;
}

export function ArtefactGrid({ artefacts }: Props) {
  const tilesByGroup = collectTilesByGroup(artefacts);
  const hasAny = GROUP_ORDER.some((g) => (tilesByGroup[g]?.length ?? 0) > 0);

  if (!hasAny) {
    return (
      <p className="rounded-lg bg-card p-4 text-sm text-text-muted shadow-sm ring-1 ring-ring">
        No artefacts yet. As the campaign progresses — brief, blueprint, market map, assessments,
        interview guides — each will appear as a tile here.
      </p>
    );
  }

  return (
    <section data-testid="artefact-grid" className="space-y-6">
      {GROUP_ORDER.map((group) => {
        const tiles = tilesByGroup[group] ?? [];
        if (tiles.length === 0) return null;
        return (
          <div key={group} data-artefact-group={group}>
            <header className="mb-2 flex items-baseline gap-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                {GROUP_LABELS[group]}
              </h2>
              <p className="text-xs text-text-faint">{GROUP_BLURBS[group]}</p>
            </header>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {tiles.map((tile) => (
                <ArtefactTile key={tile.key} tile={tile} />
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function collectTilesByGroup(artefacts: CampaignArtefact[]): Record<ArtefactGroup, Tile[]> {
  const acc: Record<ArtefactGroup, Tile[]> = {
    DEFINE: [],
    SOURCE: [],
    ENGAGE: [],
    ASSESS: [],
  };

  const aggregateBuckets = new Map<ArtefactKind, CampaignArtefact[]>();

  for (const artefact of artefacts) {
    if (AGGREGATE_KINDS.has(artefact.kind)) {
      const bucket = aggregateBuckets.get(artefact.kind) ?? [];
      bucket.push(artefact);
      aggregateBuckets.set(artefact.kind, bucket);
      continue;
    }
    acc[artefact.group].push(toTile(artefact));
  }

  for (const [kind, items] of aggregateBuckets) {
    const tile = aggregateTile(kind, items);
    if (tile) acc[items[0].group].push(tile);
  }

  return acc;
}

function toTile(artefact: CampaignArtefact): Tile {
  return {
    key: artefact.id,
    kind: artefact.kind,
    title: artefact.title,
    url: artefact.url ?? null,
    status: artefact.status ?? null,
    versionCount: artefact.versionCount ?? null,
    updatedAt: artefact.updatedAt ?? null,
    outOfSync: artefact.outOfSync === true,
  };
}

function aggregateTile(kind: ArtefactKind, items: CampaignArtefact[]): Tile | null {
  if (items.length === 0) return null;
  const staleCount = items.filter((a) => a.outOfSync === true).length;
  const newest = items
    .map((a) => a.updatedAt)
    .filter((s): s is string => typeof s === "string")
    .sort()
    .at(-1);
  const url = items.find((a) => a.url)?.url ?? null;
  return {
    key: `aggregate:${kind}`,
    kind,
    title: AGGREGATE_TITLE[kind] ?? kind,
    url,
    status: null,
    versionCount: null,
    updatedAt: newest ?? null,
    outOfSync: staleCount > 0,
    itemCount: items.length,
    staleCount,
  };
}

function ArtefactTile({ tile }: { tile: Tile }) {
  const Icon = ICON_FOR_KIND[tile.kind] ?? FileText;
  const state = stateFor(tile);

  return (
    <article
      data-artefact-id={tile.key}
      data-artefact-kind={tile.kind}
      data-artefact-state={state.tag}
      className={`flex h-full flex-col gap-2 rounded-lg bg-card p-3 shadow-sm ring-1 transition-colors ${
        tile.outOfSync ? "ring-amber-300 dark:ring-amber-800" : "ring-ring hover:ring-border-strong"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Icon size={14} className="shrink-0 text-text-faint" />
          <h3 className="truncate text-sm font-medium text-text-primary">{tile.title}</h3>
        </div>
        {typeof tile.versionCount === "number" && tile.versionCount > 0 && (
          <span className="shrink-0 text-xs text-text-faint" data-artefact-version>
            v{tile.versionCount}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
        <StateLine state={state} />
        {typeof tile.itemCount === "number" && (
          <span data-artefact-aggregate-count>
            {tile.itemCount} {tile.itemCount === 1 ? "item" : "items"}
            {typeof tile.staleCount === "number" && tile.staleCount > 0 && (
              <span className="ml-1 text-amber-600 dark:text-amber-400">
                · {tile.staleCount} stale
              </span>
            )}
          </span>
        )}
        {tile.updatedAt && (
          <span className="text-text-faint">· {formatRelative(tile.updatedAt)}</span>
        )}
      </div>

      {tile.outOfSync && (
        <div>
          <OutOfSyncBadge />
        </div>
      )}

      <div className="mt-auto flex items-center justify-end pt-1">
        {tile.url ? (
          <Link
            to={tile.url}
            className="rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
            data-artefact-action={tile.outOfSync ? "regenerate" : "open"}
          >
            {tile.outOfSync ? "Regenerate ↻" : "Open ›"}
          </Link>
        ) : (
          <span className="text-xs text-text-faint">No action available</span>
        )}
      </div>
    </article>
  );
}

type StateTag = "generating" | "error" | "approved" | "locked" | "stale" | "draft" | "ready";

interface State {
  tag: StateTag;
  label: string;
  icon?: LucideIcon;
  tone: "muted" | "warn" | "danger" | "ok";
}

function stateFor(tile: Tile): State {
  const status = (tile.status ?? "").toLowerCase();
  if (status === "generating") {
    return { tag: "generating", label: "Generating…", icon: Loader2, tone: "muted" };
  }
  if (status === "error") {
    return { tag: "error", label: "Generation failed", icon: AlertCircle, tone: "danger" };
  }
  if (tile.outOfSync) {
    return { tag: "stale", label: "Stale", tone: "warn" };
  }
  if (status.includes("approved")) {
    return { tag: "approved", label: "Approved", icon: CheckCircle2, tone: "ok" };
  }
  if (status.includes("locked")) {
    return { tag: "locked", label: "Locked", icon: CheckCircle2, tone: "ok" };
  }
  if (tile.versionCount && tile.versionCount > 0) {
    return { tag: "draft", label: "Draft", tone: "muted" };
  }
  return { tag: "ready", label: "Ready", tone: "muted" };
}

function StateLine({ state }: { state: State }) {
  const toneClass =
    state.tone === "ok"
      ? "text-emerald-700 dark:text-emerald-400"
      : state.tone === "warn"
        ? "text-amber-700 dark:text-amber-400"
        : state.tone === "danger"
          ? "text-rose-700 dark:text-rose-400"
          : "text-text-muted";
  const Icon = state.icon;
  return (
    <span className={`inline-flex items-center gap-1 ${toneClass}`} data-artefact-state-label>
      {Icon && <Icon size={12} className={state.tag === "generating" ? "animate-spin" : ""} />}
      {state.label}
    </span>
  );
}
